/**
 * ConversationListSheet — Plan 05-08b.
 *
 * BottomSheet listing past conversations from useConversations.
 * Each row has a "Slett" delete trigger that opens DeleteConversationSheet.
 *
 * Empty state: "Ingen samtaler ennå" (i18n assistant.conversations.empty).
 *
 * Replicates Phase 4 SweepConfirmSheet pattern for per-row delete.
 *
 * The per-row Slett trigger is `text-bad`, the same destructive token
 * `Button variant="danger"` and DeleteConversationSheet's confirm fill use.
 * It was `text-error` until issue #89: no `--color-error` token exists, and
 * Tailwind v4 drops a candidate it cannot resolve instead of erroring, so the
 * class compiled to nothing and the trigger painted inherited ink — visually
 * a second neutral row label, with none of the destructive signal the UI spec
 * asks for. Exactly the failure issue #78 fixed one file over, in
 * DeleteConversationSheet's `bg-error`. src/styles/no-dead-tailwind-class.test.ts
 * now compiles every className against the real @theme block so the next one
 * fails the suite instead of shipping.
 *
 * T-05-08b-DELETE-CONFIRM: delete trigger opens DeleteConversationSheet
 * which enforces two-step confirmation.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { EmptyState, LoadingState } from '../../components/primitives';
import { useConversations } from './useConversations';
import { DeleteConversationSheet } from './DeleteConversationSheet';

interface ConversationListSheetProps {
  open: boolean;
  onClose: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

export function ConversationListSheet({
  open,
  onClose,
  onSelectConversation,
}: ConversationListSheetProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useConversations({ enabled: open });
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  return (
    <>
      <BottomSheet
        open={open}
        onClose={onClose}
        title={t('assistant.conversations.title')}
      >
        <div data-testid="assistant-conversation-list-sheet">
          {/* Plan 06-02 Task 2: replace the ellipsis spinner with the
              warm-skeleton <LoadingState> primitive while
              /api/assistant/conversations resolves. */}
          {isLoading ? <LoadingState lines={2} /> : null}

          {!isLoading && data?.conversations.length === 0 && (
            // Plan 06-02 Task 2: <EmptyState> driven by the
            // assistant.conversations.empty.{heading,body} object that 06-01
            // migrated from string → object. Outer wrapper keeps the existing
            // data-testid so the conversation-list-sheet contract is intact.
            <div data-testid="assistant-conversations-empty">
              <EmptyState
                variant="inline"
                heading={t('assistant.conversations.empty.heading')}
                body={t('assistant.conversations.empty.body')}
              />
            </div>
          )}

          {!isLoading && data && data.conversations.length > 0 && (
            <ul className="space-y-3">
              {data.conversations.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 min-h-11">
                  <button
                    type="button"
                    data-testid={`assistant-conversation-open-${c.id}`}
                    className="text-base flex-1 truncate text-left min-h-11"
                    onClick={() => {
                      onSelectConversation?.(c.id);
                      onClose();
                    }}
                  >
                    {c.title ?? c.created_at.slice(0, 10)}
                  </button>
                  <button
                    type="button"
                    data-testid={`assistant-conversation-delete-${c.id}`}
                    className="text-sm text-bad font-medium min-h-11 min-w-11 px-2"
                    onClick={() => setPendingDelete(c.id)}
                  >
                    {t('assistant.conversations.delete_confirm.confirm')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </BottomSheet>

      {pendingDelete !== null && (
        <DeleteConversationSheet
          id={pendingDelete}
          open
          onClose={() => setPendingDelete(null)}
        />
      )}
    </>
  );
}
